import * as _ from "lodash";

import type {
    IAutoCompleteAppItem,
    IAutoCompleteCategoryItem,
    IAutoCompleteItem,
    IAutoCompleteKeywordItem,
    IAutoCompleteResponse,
    IAutoCompleteSiteItem,
    IAutoCompleteUniversalResponse,
} from "app/@types/autocomplete";
import type { AppStoreStr } from "app/@types/userdata";
import { AppStore } from "app/@types/userdata";
import categoryService from "app/common/services/categoryService";
import searchTypesHelperService, { SearchTypes } from "app/common/services/searchTypesHelperService";
import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultFetchService } from "app/services/fetchService";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { stateService } from "app/user-data/StateService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { AssetsService } from "./AssetsService";
import { UserCustomCategoryService } from "./category/userCustomCategoryService";

type AutocompleteSuggestionProps = {
    query: string;
    searchTypeStr: string;
    appStore?: AppStoreStr;
    resultSize?: number;
    excludeList?: string[];
    excludeUserData?: boolean;
    baseUrl?: string;
};

/**
 * Created by dannyr on 20/05/2016.
 */

interface IAutoCompleteService {
    getAutoCompleteSuggestions: (props: AutocompleteSuggestionProps) => Promise<IAutoCompleteResponse>;
    getCategoriesSection: (query: string) => any;
}
class SimpleCache {
    cache: any = {};

    put(key: string, item: FixmeType) {
        this.cache[key] = item;
    }

    get(key: string) {
        return this.cache[key] || null;
    }

    clear() {
        this.cache = {};
    }
}

const autoCompleteCache = new SimpleCache();

function limitResultCount(data: IAutoCompleteResponse, limit: number) {
    if (_.isArray(data)) {
        const res = data as any[];
        data = _.take(res, limit);
    } else if (_.isObject(data)) {
        const res = data;
        res.Apps = _.take(res.Apps, limit);
        res.Domains = _.take(res.Domains, limit);
        data = res;
    } else {
        data = {
            Apps: [],
            Domains: [],
            Categories: [],
            Keywords: [],
        };
    }

    return data;
}

function excludeSearchResults(items: IAutoCompleteItem[], excludeList: string[]) {
    return items.filter((item: IAutoCompleteItem) => {
        let itemKey: string | null = null;
        switch (item.type) {
            case "website":
            case "app":
                const appItem = item as IAutoCompleteAppItem;
                itemKey = appItem.id;
                break;
        }
        // @ts-ignore FIXME: strict migration - 'itemKey' is 'string | null' — Array.indexOf(null) returns -1, preserving original behavior of skipping non-website/non-app items
        return excludeList.indexOf(itemKey) == -1;
    });
}

const trafficOverviewSearchParams: Record<string, unknown> = {
    Keywords_filters: null,
    groupedKeywords_filters: null,
    groupedKeywords_orderby: null,
    Ads_filters: null,
    IncludeOrganic: null,
    IncludePaid: null,
    IncludeBranded: null,
    IncludeNoneBranded: null,
    IncludeNewKeywords: null,
    IncludeTrendingKeywords: null,
    IncludeQuestions: null,
    limits: null,
    BooleanSearchTerms: null,
    search: null,
    selectedDomain: null,
    orderBy: null,
    ExcludeTerms: null,
    ExcludeUrls: null,
    IncludeUrls: null,
    IncludeTerms: null,
    family: null,
    source: null,
    selectedPhrase: null,
    volumeFromValue: null,
    volumeToValue: null,
    cpcFromValue: null,
    cpcToValue: null,
    serp: null,
};

function setDomainUrl(item: FixmeType) {
    const params = { ...proNavigator.getParams(), ...trafficOverviewSearchParams } as any;
    params.key = item.name;
    if (_.get(proNavigator.current(), "pageId.subSubSection") === "popular") {
        params.selectedSite = item.name;
    }
    item.url =
        proNavigator.isWebsiteAnalysis() && !proNavigator.isHomePage() && !item.isVirtual
            ? // @ts-ignore FIXME: strict migration - 'name' is 'string | undefined' on IRouterState — extends external ReactStateDeclaration where name is optional, runtime always has a current state name
              proNavigator.href(proNavigator.current().name, params)
            : stateService.buildItemUrl(item.type, item.name);
}

function setAppUrl(item: FixmeType) {
    const params = proNavigator.getParams();
    const keyAppStore = item.store == "google" ? 0 : 1;
    params.appId = keyAppStore + "_" + item.id;
    item.url =
        proNavigator.isAppAnalysis() && !proNavigator.isHomePage()
            ? // @ts-ignore FIXME: strict migration - 'name' is 'string | undefined' on IRouterState — extends external ReactStateDeclaration where name is optional, runtime always has a current state name
              proNavigator.href(proNavigator.current().name, params)
            : stateService.buildItemUrl(item.type, item.id, [], item.store);
}

function setKeywordUrl(item: FixmeType) {
    // the Keywords section, replaces it's URL if it is in the GooglePlayKeywords module
    if (proNavigator.isGooglePlayKeywordAnalysis()) {
        setGooglePlayKeywordUrl(item);
    } else {
        setKeywordAnalysisUrl(item);
    }
}

function setKeywordAnalysisUrl(item: FixmeType) {
    const _params = proNavigator.getParams();
    const _itemName = item.Keywords ? "*" + item.Id : item.name;
    item.url =
        proNavigator.isKeywordAnalysis() && !proNavigator.isHomePage()
            ? // @ts-ignore FIXME: strict migration - 'name' is 'string | undefined' on IRouterState — extends external ReactStateDeclaration where name is optional, runtime always has a current state name
              proNavigator.href(proNavigator.current().name, {
                  ..._params,
                  keyword: _itemName,
                  mainItem: _itemName,
              })
            : proNavigator.href("organicsearch_keywordAnalysis_overview_2", {
                  ...swSettings.components.KeywordAnalysis.defaultParams,
                  keyword: _itemName,
                  mainItem: _itemName,
              });
}

function setGooglePlayKeywordUrl(item: FixmeType) {
    const params = proNavigator.getParams();
    item.url =
        proNavigator.isGooglePlayKeywordAnalysis() && !proNavigator.isHomePage()
            ? // @ts-ignore FIXME: strict migration - 'name' is 'string | undefined' on IRouterState — extends external ReactStateDeclaration where name is optional, runtime always has a current state name
              proNavigator.href(proNavigator.current().name, { ...params, keyword: item.name })
            : proNavigator.href("keywords-analysis", {
                  ...swSettings.components.AppKeywordAnalysis.defaultParams,
                  keyword: item.name,
              });
}

const ignoreIndustryAnalysisParams: Record<string, unknown> = {
    channel: null,
    excludeBranded: null,
    search: null,
    BooleanSearchTerms: null,
    IncludeTrendingKeywords: null,
    IncludeNewKeywords: null,
    IncludeQuestions: null,
};

function setIndustryUrl(category: string) {
    const params = { ...proNavigator.getParams(), ...ignoreIndustryAnalysisParams };
    return proNavigator.isIndustryAnalysis()
        ? // @ts-ignore FIXME: strict migration - 'name' is 'string | undefined' on IRouterState — extends external ReactStateDeclaration where name is optional, runtime always has a current state name
          proNavigator.href(proNavigator.current().name, Object.assign(params, { category }))
        : proNavigator.href("digitalsuite_marketresearch_webmarketanalysis_overview", {
              ...swSettings.components.IndustryAnalysisOverview.defaultParams,
              category,
          });
}

async function getAutoCompleteSuggestions({
    query,
    searchTypeStr,
    appStore,
    resultSize = 10,
    excludeList,
    excludeUserData,
    baseUrl = "",
}: AutocompleteSuggestionProps): Promise<any> {
    if (!query.trim()) {
        return [];
    }
    const cacheKey = `${query}-${searchTypeStr}`;
    const searchType = searchTypesHelperService.standardSearchType(searchTypeStr);
    //Get keywords groups
    const _userKeywordsGroups = await keywordGroupsQueryClient.getUserGroups();
    // try cache:
    let cache = autoCompleteCache.get(cacheKey);
    let cacheData;
    if (cache) {
        switch (searchType) {
            case SearchTypes.WEBSITE:
                cacheData = cache as IAutoCompleteSiteItem[];
                cacheData.forEach((item) => setDomainUrl(item));
                if (excludeList && excludeList.length) {
                    cache = excludeSearchResults(cacheData, excludeList) as IAutoCompleteSiteItem[];
                }
                break;
            case SearchTypes.MOBILE:
                cacheData = cache as IAutoCompleteAppItem[];
                cacheData.forEach((item) => setAppUrl(item));
                if (excludeList && excludeList.length) {
                    cache = excludeSearchResults(cacheData, excludeList) as IAutoCompleteSiteItem[];
                }
                break;
            case SearchTypes.UNIVERSAL:
                cacheData = cache as IAutoCompleteUniversalResponse;
                cacheData.Domains.forEach((item) => setDomainUrl(item));
                cacheData.Apps.forEach((item) => setAppUrl(item));
                cacheData.Keywords.forEach((item) => setKeywordUrl(item));
                if (excludeList && excludeList.length) {
                    // @ts-ignore FIXME: strict migration - 'IAutoCompleteUniversalResponse' not assignable to 'IAutoCompleteItem[]' — legacy code passes universal response object to array filter, preserving original behavior
                    cache = excludeSearchResults(cacheData, excludeList) as IAutoCompleteSiteItem[];
                }
                break;
            case SearchTypes.KEYWORD:
                cacheData = cache as IAutoCompleteKeywordItem[];
                cacheData.forEach((item) => {
                    setKeywordUrl(item);
                    item.icon = "icon sw-icon-keyword";
                });
                //Unless defined to avoid - user's keywords groups will be added on the top of the list.
                if (!excludeUserData) {
                    if (_userKeywordsGroups) {
                        // @ts-ignore FIXME: strict migration - 'IUserKeywordGroup' not assignable to 'IAutoCompleteKeywordItem' — legacy code intentionally mixes user keyword groups into autocomplete results
                        cacheData.unshift(..._userKeywordsGroups);
                    }
                    cacheData = _.slice(cacheData, 0, 10);
                }
                break;
            case SearchTypes.INDUSTRY:
                cache = getCategoriesSection(query);
                break;
            case SearchTypes.PLAYKEYWORD:
            default:
                cache = null;
                break;
        }
        return cache;
    }
    //End cahe

    //Define data fetch from server (or local for custom categories and keywords groups)
    let url = "websites";
    const params: { term: string; store: AppStore | null; size: number } = {
        term: query.toLowerCase(),
        store: null,
        size: resultSize,
    };

    //Define autocomplete getch url
    switch (searchType) {
        case SearchTypes.WEBSITE:
            url = "websites";
            break;

        case SearchTypes.KEYWORD:
            url = "keywords";
            break;

        case SearchTypes.MOBILE:
            url = "apps";
            switch (appStore) {
                case "google":
                    params.store = AppStore.Google;
                    break;
                case "apple":
                    params.store = AppStore.Apple;
                    break;
            }
            break;

        case SearchTypes.UNIVERSAL:
            url = "universal";
            break;
        case SearchTypes.PLAYKEYWORD:
            break;
        case SearchTypes.INDUSTRY:
            return getCategoriesSection(query);
        default:
            return null;
    }

    const rawData = await DefaultFetchService.getInstance().get<IAutoCompleteResponse>(
        `${baseUrl}/autocomplete/` + url,
        params,
    );

    // limit
    const data = limitResultCount(rawData, resultSize);
    let dataItems;
    // process result
    switch (searchType) {
        case SearchTypes.WEBSITE:
            dataItems = data as IAutoCompleteSiteItem[];
            dataItems.forEach((item) => (item.type = "website"));
            autoCompleteCache.put(cacheKey, dataItems);
            if (excludeList && excludeList.length) {
                dataItems = excludeSearchResults(dataItems, excludeList) as IAutoCompleteSiteItem[];
            }
            dataItems.forEach((item) => (item.url = stateService.buildItemUrl(item.type, item.name)));
            break;
        case SearchTypes.MOBILE:
            dataItems = data as IAutoCompleteAppItem[];
            dataItems.forEach((item) => (item.type = "app"));
            autoCompleteCache.put(cacheKey, dataItems);
            if (excludeList && excludeList.length) {
                dataItems = excludeSearchResults(dataItems, excludeList) as IAutoCompleteAppItem[];
            }
            dataItems.forEach((item) => (item.url = stateService.buildItemUrl(item.type, item.id, [], item.store)));
            break;

        case SearchTypes.UNIVERSAL:
            dataItems = data as IAutoCompleteUniversalResponse;
            dataItems.Domains.forEach((item) => (item.type = "website"));
            dataItems.Apps.forEach((item) => (item.type = "app"));
            dataItems.Keywords.forEach((item) => (item.type = "keyword"));
            if (dataItems.Domains.length === 0) {
                const defaultSiteItem: IAutoCompleteSiteItem = {
                    type: "website",
                    name: proNavigator.getValidSearchTerm(query, true),
                    isVirtual: false,
                    image: AssetsService.assetUrl("/Images/autocomplete-default.png"),
                };
                dataItems.Domains.push(defaultSiteItem);
            }
            if (dataItems.Keywords.length === 0) {
                const defaultKeywordItem: IAutoCompleteKeywordItem = {
                    type: "keyword",
                    name: query,
                    isVirtual: false,
                    icon: "icon svg-suite-home--keyword svg-suite-home--keyword-dims",
                };
                dataItems.Keywords.push(defaultKeywordItem);
            }
            dataItems.Domains.forEach((item) => setDomainUrl(item));
            dataItems.Apps.forEach((item) => setAppUrl(item));
            dataItems.Keywords.forEach((item) => {
                setKeywordUrl(item);
                item.icon = "icon svg-suite-home--keyword svg-suite-home--keyword-dims";
            });
            if (_userKeywordsGroups) {
                // @ts-ignore FIXME: strict migration - 'IUserKeywordGroup' not assignable to 'IAutoCompleteKeywordItem' — legacy code intentionally mixes user keyword groups into autocomplete results
                dataItems.Keywords.unshift(..._userKeywordsGroups);
            }
            autoCompleteCache.put(cacheKey, dataItems);
            break;

        case SearchTypes.KEYWORD:
            dataItems = Array.isArray(data) ? data : [];
            dataItems.forEach((item) => {
                setKeywordUrl(item);
                // @ts-ignore FIXME: strict migration - 'icon' missing on union 'IAutoCompleteAppItem | IAutoCompleteSiteItem | IAutoCompleteKeywordItem' — KEYWORD branch only sees keyword items at runtime, but type union from IAutoCompleteResponse is wider
                item.icon = "icon sw-icon-keyword";
                item.image = undefined;
            });
            if (!excludeUserData) {
                if (_userKeywordsGroups) {
                    // @ts-ignore FIXME: strict migration - 'IUserKeywordGroup' not assignable to intersection type — legacy code intentionally mixes user keyword groups into autocomplete results
                    dataItems.unshift(..._userKeywordsGroups);
                }
                // @ts-ignore FIXME: strict migration - union array type not assignable to lodash List<IAutoCompleteAppItem> — _.slice handles any array shape at runtime, lodash typings are stricter than needed
                dataItems = _.slice(dataItems, 0, 10);
            }
            break;
        case SearchTypes.PLAYKEYWORD:
            dataItems = [];
            break;
    }

    return dataItems;
}

function getCategoriesSection(query: string) {
    query = query.toLowerCase();
    const _searchItems: IAutoCompleteCategoryItem[] = [];
    //Search in user's custom categories
    UserCustomCategoryService.getCustomCategories().forEach(function (category) {
        const categoryName = i18nFilter()(category.text);
        if (categoryName.toLowerCase().indexOf(query) != -1) {
            _searchItems.push({
                type: "industry",
                name: categoryName,
                category: category.id,
                categoryId: category.categoryId,
                icon: category.icon,
                url: setIndustryUrl(category.id),
                categoryObject: category,
            });
        }
    });
    //Search in all high-order categories
    categoryService.getCategories().forEach(function (category) {
        const categoryName = i18nFilter()(category.text);
        let showSons = false;
        if (categoryName.toLowerCase().indexOf(query) != -1) {
            _searchItems.push({
                type: "industry",
                name: categoryName,
                category: category.id,
                icon: category.icon,
                url: setIndustryUrl(category.name || category.id),
                categoryObject: category,
            });
            showSons = true;
        }
        //If there are children - search in all children
        if (category.sons) {
            category.children.forEach(function (subcategory: FixmeType) {
                const subCategoryName = subcategory.getText();
                if (showSons || subCategoryName.toLowerCase().indexOf(query) != -1) {
                    _searchItems.push({
                        type: "industry",
                        name: subCategoryName,
                        category: subcategory.id,
                        icon: category.icon,
                        url: setIndustryUrl(subcategory.id),
                        categoryObject: category,
                    });
                }
            });
        }
    });
    return _.slice(_searchItems, 0, 10);
}

export const autoCompleteService: IAutoCompleteService = {
    getAutoCompleteSuggestions,
    getCategoriesSection,
};
